
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import "./Hero.css";

// =========================================================
// PROJECT IMAGES
// Location: src/images/HeroSectionImages
// =========================================================

// Dreamland Bites
import dreamlandWeb from "../../images/HeroSectionImages/dreamland-web.webp";
import dreamlandAdmin from "../../images/HeroSectionImages/dreamland-admin-dashboard.webp";
import dreamlandAI from "../../images/HeroSectionImages/dreamland-ai-assistant.webp";

// Coconut Multiproduct
import coconutWeb from "../../images/HeroSectionImages/coconut-web.webp";
import daabJall from "../../images/HeroSectionImages/daab-jall-google-play.webp";

// Jabala MIS
import jabalaDashboard from "../../images/HeroSectionImages/Jabala_SupreAdmin_DashBoard.webp";
import jabalaLogin from "../../images/HeroSectionImages/Jabala_DashBoard.webp";

// Internal Products
import mathGenius from "../../images/HeroSectionImages/math-genius-google-play.webp";
import crmDashboard from "../../images/HeroSectionImages/BairagiConsultsDashboard.webp";
import pdfPageManager from "../../images/HeroSectionImages/pdf-page-manager.webp";

// Research and Development
import pressMed from "../../images/HeroSectionImages/pressmed-prototype.webp";
import jewelleryTryOn from "../../images/HeroSectionImages/jewellery-tryon-concept.webp";

// =========================================================
// CASE STUDIES AND PROJECT PORTFOLIO
// =========================================================

export const caseStudies = [
  // -------------------------------------------------------
  // CLIENT PROJECTS
  // -------------------------------------------------------

  {
    title: "Dreamland Bites",
    subtitle: "Premium Bakery Ecommerce & Admin Platform",
    tech: "React • FastAPI • PostgreSQL • Ecommerce",
    image: dreamlandWeb,
    additionalImages: [dreamlandAdmin, dreamlandAI],
    label: "CLIENT PROJECT",
    category: "Ecommerce",
    link: "https://dreamlandbitess.com/",
    external: true,
    linkLabel: "View Live Project",
  },

  {
    title: "Coconut Multiproduct",
    subtitle: "Ecommerce Website & Android Application",
    tech: "Web • Android • Ecommerce",
    image: coconutWeb,
    additionalImages: [daabJall],
    label: "CLIENT PROJECT",
    category: "Web & Mobile",
    link: "https://coconutmultiproduct.com/",
    external: true,
    linkLabel: "View Live Project",
    secondaryLink:
      "https://play.google.com/store/apps/details?id=com.coconutapp",
    secondaryLabel: "Android App on Google Play",
  },

  {
    title: "Jabala MIS",
    subtitle: "NGO Rehabilitation & Management Information System",
    tech: "React • FastAPI • PostgreSQL • MIS",
    image: jabalaDashboard,
    additionalImages: [jabalaLogin],
    label: "CLIENT PROJECT",
    category: "Enterprise MIS",
    link: "/case-studies",
    external: false,
    linkLabel: "View Case Studies",
  },

  // -------------------------------------------------------
  // INTERNAL PRODUCTS
  // -------------------------------------------------------

  {
    title: "Math Genius",
    subtitle: "Published Android Educational Application",
    tech: "Android • Educational Technology • Google Play",
    image: mathGenius,
    additionalImages: [],
    label: "INTERNAL PRODUCT",
    category: "Mobile Application",
    link:
      "https://play.google.com/store/apps/details?id=com.BairagiConsultants.MathQuiz",
    external: true,
    linkLabel: "View on Google Play",
  },

  {
    title: "Business CRM Dashboard",
    subtitle: "CRM Analytics & Business Management Dashboard",
    tech: "Dashboard • Analytics • Business Management",
    image: crmDashboard,
    additionalImages: [],
    label: "INTERNAL PRODUCT",
    category: "CRM & Analytics",
    link: "/case-studies",
    external: false,
    linkLabel: "View Portfolio",
  },

  {
    title: "PDF Page Manager",
    subtitle: "Document Management & PDF Processing Utility",
    tech: "PDF Processing • Document Management",
    image: pdfPageManager,
    additionalImages: [],
    label: "INTERNAL TOOL",
    category: "Productivity",
    link: "/case-studies",
    external: false,
    linkLabel: "View Portfolio",
  },

  // -------------------------------------------------------
  // INTERNAL RESEARCH AND DEVELOPMENT
  // -------------------------------------------------------

  {
    title: "PressMed",
    subtitle: "Digital Healthcare & E-Prescription Prototype",
    tech: "Healthcare • Digital Prescription • AI Exploration",
    image: pressMed,
    additionalImages: [],
    label: "INTERNAL R&D",
    category: "Healthcare",
    link: "/case-studies",
    external: false,
    linkLabel: "View Portfolio",
  },

  {
    title: "Jewellery Virtual TryOn",
    subtitle: "Augmented Reality Jewellery Shopping Concept",
    tech: "AR • Virtual Try-On • Ecommerce",
    image: jewelleryTryOn,
    additionalImages: [],
    label: "INTERNAL R&D",
    category: "AR & Retail",
    link: "/case-studies",
    external: false,
    linkLabel: "View Portfolio",
  },
];

// =========================================================
// HOMEPAGE: FEATURED CLIENT PROJECTS ONLY
// =========================================================

const featuredCaseStudies = caseStudies.filter(
  (project) => project.label === "CLIENT PROJECT"
);

// =========================================================
// HERO COMPONENT
// =========================================================

const Hero = () => {
  const navigate = useNavigate();

  const [activeImage, setActiveImage] = useState(0);

  // =======================================================
  // CONSULTATION NAVIGATION
  // =======================================================

  const handleConsultation = () => {
    navigate("/contact?service=consultation");
  };

  // =======================================================
  // CASE STUDY ROTATION
  // =======================================================

  useEffect(() => {
    if (featuredCaseStudies.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setActiveImage(
        (prev) => (prev + 1) % featuredCaseStudies.length
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // =======================================================
  // HERO ANIMATION
  // =======================================================

  useEffect(() => {
    const timer = setTimeout(() => {
      const elements = document.querySelectorAll(
        ".hero-container .animate-on-load"
      );

      elements.forEach((element, index) => {
        setTimeout(() => {
          element.classList.add("animate-visible");
        }, index * 160);
      });
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // =======================================================
  // ACTIVE CASE STUDY
  // =======================================================

  const activeCaseStudy = featuredCaseStudies[activeImage];

  if (!activeCaseStudy) {
    return null;
  }

  // =======================================================
  // PROJECT LINK RENDERER
  // =======================================================

  const renderProjectLink = (project) => {
    if (!project.link) {
      return null;
    }

    const label = project.linkLabel || "View Project";

    if (project.external) {
      return (
        <a
          href={project.link}
          target="_blank"
          rel="noopener noreferrer"
          className="case-study-link"
          aria-label={`${label}: ${project.title}`}
        >
          {label} →
        </a>
      );
    }

    return (
      <Link
        to={project.link}
        className="case-study-link"
        aria-label={`${label}: ${project.title}`}
      >
        {label} →
      </Link>
    );
  };

  // =======================================================
  // JSX
  // =======================================================

  return (
    <section
      className="hero-container"
      aria-labelledby="homepage-main-heading"
    >
      {/* ================================================
          BACKGROUND GLOWS
      ================================================ */}

      <div
        className="hero-glow hero-glow-1"
        aria-hidden="true"
      />

      <div
        className="hero-glow hero-glow-2"
        aria-hidden="true"
      />

      {/* ================================================
          LEFT CONTENT
      ================================================ */}

      <div className="hero-content">
        {/* HERO LABEL */}

        <p className="hero-label animate-on-load">
          CUSTOM SOFTWARE • AI SOLUTIONS • BUSINESS AUTOMATION
        </p>

        {/* PRIMARY SEO H1 */}

        <h1
          id="homepage-main-heading"
          className="hero-title animate-on-load"
        >
          Custom Software Development

          <br />

          <span className="hero-highlight">
            For AI, Ecommerce
          </span>

          <br />

          & Enterprise Platforms
        </h1>

        {/* DESCRIPTION */}

        <p className="hero-subtitle animate-on-load">
          We build scalable web applications, AI systems,
          mobile apps, CRM, MIS, ecommerce platforms and
          enterprise software using React, FastAPI,
          PostgreSQL and modern cloud architecture.
        </p>

        {/* ================================================
            CTA
        ================================================ */}

        <div className="hero-cta animate-on-load">
          <button
            type="button"
            className="primary-button"
            onClick={handleConsultation}
          >
            Book Free Consultation

            <span
              className="button-arrow"
              aria-hidden="true"
            >
              →
            </span>
          </button>

          <Link
            to="/case-studies"
            className="secondary-button"
          >
            View Case Studies
          </Link>
        </div>

        {/* ================================================
            TRUST / SERVICE TAGS
        ================================================ */}

        <div className="hero-tags animate-on-load">
          <span>✔ Custom Business Applications</span>

          <span>✔ Ecommerce Platforms</span>

          <span>✔ Android Mobile Applications</span>

          <span>✔ CRM & MIS Solutions</span>

          <span>✔ Dashboard Solutions</span>

          <span>✔ Cloud Deployment</span>
        </div>
      </div>

      {/* ================================================
          RIGHT SIDE
      ================================================ */}

      <div className="hero-image animate-on-load">
        {/* ================================================
            FEATURED PROJECT CARD
        ================================================ */}

        <div className="case-study-card">
          <div className="case-study-content">
            {/* PROJECT TYPE */}

            <span className="case-study-label">
              {activeCaseStudy.label}
            </span>

            {/* PROJECT NAME */}

            <h3>{activeCaseStudy.title}</h3>

            {/* PROJECT SUBTITLE */}

            <p>{activeCaseStudy.subtitle}</p>

            {/* TECHNOLOGY */}

            <div className="case-study-tech">
              {activeCaseStudy.tech}
            </div>

            {/* PRIMARY LINK */}

            {renderProjectLink(activeCaseStudy)}

            {/* SECONDARY LINK: GOOGLE PLAY */}

            {activeCaseStudy.secondaryLink && (
              <a
                href={activeCaseStudy.secondaryLink}
                target="_blank"
                rel="noopener noreferrer"
                className="case-study-link"
                style={{
                  display: "inline-block",
                  marginLeft: "12px",
                  marginTop: "10px",
                  fontSize: "13px",
                }}
                aria-label={
                  `${activeCaseStudy.secondaryLabel}: ` +
                  activeCaseStudy.title
                }
              >
                {activeCaseStudy.secondaryLabel} →
              </a>
            )}
          </div>

          {/* PROJECT IMAGE */}

          {activeCaseStudy.image && (
            <img
              src={activeCaseStudy.image}
              alt={
                `${activeCaseStudy.title} software project ` +
                "by Bairagi Consultants"
              }
              className="dashboard-preview-image"
              loading="eager"
              decoding="async"
            />
          )}
        </div>

        {/* ================================================
            CAPABILITY SUMMARY
        ================================================ */}

        <div className="stats-card animate-on-load">
          <div className="stats-item">
            <h4>
              Web • Mobile • CRM • MIS
            </h4>

            <p>
              Custom business software solutions
            </p>
          </div>

          <div className="stats-item">
            <h4>
              React • FastAPI • PostgreSQL
            </h4>

            <p>
              Modern web and backend architecture
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
